import React, {useMemo, useState} from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    IconButton,
    Tooltip,
} from '@mui/material';
import {Add, ContentCopy, Delete as DeleteIcon} from '@mui/icons-material';
import {PlaybookExampleDto} from "../../dto/PlaybookExampleDto";
import {SecondaryIconButton} from "../button/ButtonVariants";
import theme from "../../theme/theme";
import {sortExamples} from "../../utils/sortExamples";

type ExampleTableProps = {
    examples: PlaybookExampleDto[];
    selectedExampleId: string | null;
    loading: boolean;
    onSelect: (id: string | undefined) => void;
    onCopy: (id: string | undefined) => void;
    onDelete: (id: string | undefined) => void;
    onAddExample: () => void;
};

const ExampleTable: React.FC<ExampleTableProps> =
    ({
         examples,
         selectedExampleId,
         loading,
         onSelect,
         onCopy,
         onDelete,
         onAddExample,
   }) => {
        const [sortBy, setSortBy] = useState<'name' | null>(null);
        const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

        const sortedExamples = useMemo(
            () => sortExamples(examples, sortBy, sortDirection),
            [examples, sortBy, sortDirection]
        );

        const handleSort = (column: typeof sortBy) => {
            if (sortBy === column) {
                setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortBy(column);
                setSortDirection('asc');
            }
        };

        const getSortLabelSx = (column: typeof sortBy) => ({
            color: sortBy === column ? 'primary.main' : 'text.primary',
            '&:hover': {
                color: 'primary.dark',
                '& .MuiTableSortLabel-icon': { color: 'primary.dark' },
            },
            '&.Mui-active': {
                color: 'primary.main',
                '& .MuiTableSortLabel-icon': { color: 'primary.main' },
            },
        });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sortDirection={sortBy === 'name' ? sortDirection : false}
                                aria-sort={
                                    sortBy === 'name'
                                        ? sortDirection === 'asc'
                                            ? 'ascending'
                                            : 'descending'
                                        : undefined
                                }
                            >
                                <TableSortLabel
                                    active={sortBy === 'name'}
                                    direction={sortBy === 'name' ? sortDirection : 'asc'}
                                    sx={getSortLabelSx('name')}
                                    onClick={() => handleSort('name')}
                                >
                                    <strong>Name</strong>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedExamples.map((example) => (
                            <TableRow
                                key={example.gcpName}
                                hover
                                selected={selectedExampleId === example.gcpName}
                                onClick={() => onSelect(example.gcpName)}
                                onKeyDown={(e) => {
                                    if (['Enter', ' '].includes(e.key)) {
                                        e.preventDefault();
                                        onSelect(example.gcpName);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-pressed={selectedExampleId === example.gcpName}
                                sx={{
                                    cursor: 'pointer',
                                    '&:focus-visible': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '-1px',
                                    },
                                    '&:hover': {
                                        backgroundColor: theme.palette.custom.customBackgroundGray,
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.custom.customBackgroundGray,
                                        '&:hover': {
                                            backgroundColor: theme.palette.custom.customBackgroundGray,
                                        },
                                    }
                                }}
                            >
                                <TableCell>{example.displayName}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Beispiel duplizieren">
                                        <IconButton
                                            aria-label="kopieren"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCopy(example.gcpName);
                                            }}
                                            sx={{ color: theme.palette.secondary.main }}
                                        >
                                            <ContentCopy />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Beispiel löschen">
                                        <IconButton
                                            aria-label="löschen"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(example.gcpName);
                                            }}
                                            sx={{ color: theme.palette.secondary.main }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" paddingY={4}>
                <Tooltip title="Beispiel hinzufügen">
                    <SecondaryIconButton
                        aria-label="Beispiel hinzufügen"
                        size="large"
                        onClick={onAddExample}
                    >
                        <Add sx={{ width: 18, height: 18, color: 'white' }} />
                    </SecondaryIconButton>
                </Tooltip>
            </Box>
        </>
    );
};

export default ExampleTable;